import { SALES_ORDERS_PAY_URL } from 'config/app'
import { hasPermissions } from 'lib/auth'

const safeString = (v) => String(v ?? '')

export const DELIVERY_STATUS = {
  DELIVERY_STATE_PENDING: 'PENDING',
  DELIVERY_STATE_COMPLETED: 'COMPLETED',
  DELIVERY_STATE_FAILED: 'FAILED',
  DELIVERY_STATE_CANCELLED: 'CANCELLED',
}

export const ORDER_STATUS = {
  STATE_COMPLETED: 'COMPLETED',
  STATE_PENDING_FULFILMENT: 'PENDING FULFILMENT',
  STATE_PENDING_SETTLEMENT: 'PENDING SETTLEMENT',
  STATE_PENDING_CANCELLATION: 'PENDING CANCELLATION',
  STATE_CANCELLED: 'CANCELLED',
}

export const PAYMENT_MODES = {
  GOOGLE_PAY: 'Google Pay',
  APPLE_PAY: 'Apple Pay',
  CREDITTERM: 'Credit Term',
  EINVOICE: 'E Invoice',
  GEBIZ: 'Ge Biz',
  ONLINE: 'Card Payment',
  QUICK_BUY: 'Quick Buy',
  UNKNOWN: 'Unknown',
  CORPBILLING: 'Quick Buy',
}

export const TIME_SLOT_OPTIONS = [
  { text: '08:00 - 10:00', value: '08:00 - 10:00' },
  { text: '10:00 - 12:00', value: '10:00 - 12:00' },
  { text: '12:00 - 14:00', value: '12:00 - 14:00' },
  { text: '14:00 - 16:00', value: '14:00 - 16:00' },
  { text: '16:00 - 18:00', value: '16:00 - 18:00' },
  { text: '18:00 - 20:00', value: '18:00 - 20:00' },
  { text: '20:00 - 22:00', value: '20:00 - 22:00' },
]

export const ORDER_TYPE = {
  ONLINE_GROCERY: 'Online Grocery',
}

let urlObj
try {
  urlObj = new URL(SALES_ORDERS_PAY_URL)
} catch {
  throw new Error('SALES_ORDERS_PAY_URL must be an absolute URL with protocol')
}

const parts = urlObj.pathname.split('/').filter(Boolean)
const serviceName = parts[parts.length - 1]

const SALES_ORDER_PRI = 'pri-salesorder-admin'

export const hasRedeliveryAccess = () =>
  hasPermissions(SALES_ORDER_PRI, `${serviceName}/CheckoutOrder`, 'post', false)

export const hasGetClientSalesOrdersAccess = () =>
  hasPermissions(
    SALES_ORDER_PRI,
    `${serviceName}/GetClientSalesOrder`,
    'post',
    false
  )

export const hasListClientSalesOrders = () =>
  hasPermissions(
    SALES_ORDER_PRI,
    `${serviceName}/ListClientSalesOrders`,
    'post',
    false
  )

export const hasCompleteFulfilmentAccess = () =>
  hasPermissions(
    SALES_ORDER_PRI,
    `${serviceName}/CompleteFulfilment`,
    'post',
    false
  )

const makeAddressFromBilling = (saleOrderData) => {
  const b = saleOrderData?.billingAddress
  const first = b?.metaData?.FirstName || ''
  const last = b?.metaData?.LastName || ''
  const unit =
    b?.metaData?.Unit && b?.metaData?.Floor
      ? `#${b.metaData.Floor}-${b.metaData.Unit}`
      : b?.metaData?.Unit

  return {
    recipientName: `${first} ${last}`.trim(),
    address1: b?.address,
    unitNumber: unit,
    city: b?.city || 'Singapore',
    country: 'Singapore',
    postalCode: safeString(b?.postalcode || b?.pincode || ''),
    phoneNumber: b?.metaData?.Phone,
  }
}

const normalizeNumber = (value) => {
  return String(parseInt(value, 10))
}

const orderTypeMapping = {
  DELIVERY: 'SALES_ORDER_TYPE_ONLINE_GROCERY',
  B2B: 'SALES_ORDER_TYPE_ONLINE_GROCERY_B2B',
  OFFLINE: 'SALES_ORDER_TYPE_SCAN_AND_GO',
  RB_PREORDER: 'SALES_ORDER_TYPE_RB_PREORDER',
  UNKNOWN: 'SALES_ORDER_TYPE_UNSPECIFIED',
  LINKPAY: 'SALES_ORDER_TYPE_LINKPAY',
}

export const createOrderPayload = ({
  referenceNumber,
  saleOrderData,
  redeliveryItems,
  partialDiscountMethods,
  selectedCardId,
  deliveryState,
  redeliveryDate,
  redeliveryTimeSlot,
}) => {
  const resolvedPaymentMode = saleOrderData?.payments?.[0]?.mode || 'UNKNOWN'
  const orderType =
    saleOrderData?.payments?.[0]?.metaData?.orderType || 'UNKNOWN'

  let paymentMethod = {}

  switch (resolvedPaymentMode) {
    case 'GOOGLE_PAY':
    case 'APPLE_PAY':
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_SELF_MANAGED',
          selfManagedSystem: 'Adyen',
        },
        selfManagedType: 'CARD',
        cardId: selectedCardId,
      }
      break

    case 'CREDITTERM':
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_AUTO_MANAGED',
        },
        creditTerm: {
          poNumber: saleOrderData?.payments?.[0]?.metaData?.poNumber || '',
        },
        type: 'PAYMENT_METHOD_TYPE_CREDIT_TERM',
      }
      break

    case 'QUICKBUY':
    case 'CORPBILLING':
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_AUTO_MANAGED',
        },
        type: 'PAYMENT_METHOD_TYPE_QUICK_BUY',
      }
      break

    case 'EINVOICE':
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_AUTO_MANAGED',
        },
        eInvoice: {
          ministry: saleOrderData?.payments?.[0]?.metaData?.ministry || '',
          department: saleOrderData?.payments?.[0]?.metaData?.department || '',
          subBusiness:
            saleOrderData?.payments?.[0]?.metaData?.subbusiness || '',
        },
        type: 'PAYMENT_METHOD_TYPE_E_INVOICE',
      }
      break

    case 'GEBIZ':
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_AUTO_MANAGED',
        },
        geBiz: {
          poNumber: saleOrderData?.payments?.[0]?.metaData?.poNumber || '',
        },
        type: 'PAYMENT_METHOD_TYPE_GE_BIZ',
      }
      break

    default:
      paymentMethod = {
        client: {
          system: 'CLIENT_SYSTEM_AUTO_MANAGED',
        },
        type: 'PAYMENT_METHOD_TYPE_CARD',
        cardId: selectedCardId,
      }
      break
  }

  const redeliveryDateArr = redeliveryDate.split('-')

  return {
    order: {
      baseSalesOrder: {
        id: referenceNumber,
        system: 'SALES_ORDER_SYSTEM_OMS',
      },
      billingAddress: makeAddressFromBilling(saleOrderData),
      customerUid: saleOrderData?.customer?.uid || saleOrderData?.customerUid,
      fulfilments: [
        {
          client: {
            system: 'CLIENT_SYSTEM_SELF_MANAGED',
          },
          address: makeAddressFromBilling(saleOrderData),
          date: {
            year: redeliveryDateArr[0],
            month: normalizeNumber(redeliveryDateArr[1]),
            day: normalizeNumber(redeliveryDateArr[2]),
          },
          timeSlot: redeliveryTimeSlot,
          fees: [
            {
              amount: '0',
              isSubjectToGst: true,
              type: 'FEE_TYPE_DELIVERY',
            },
          ],
          items: redeliveryItems,
          ...(deliveryState ? { deliveryState } : {}),
        },
      ],
      ...(partialDiscountMethods.length > 0 && { partialDiscountMethods }),
      orderType: orderTypeMapping[orderType] || 'SALES_ORDER_TYPE_UNSPECIFIED',
      paymentMethod,
      sellers: [{ name: 'FairPrice', vendorCode: '0' }],
    },
  }
}
